import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Plus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";

// Tenant attribute picker with inline creation (Sep 2026).
//
// One control for Brand, Unit of Measure and Product Family. The
// Product Owner must never have to leave the product form to add a
// value that is missing, so the "+ Add" row lives at the bottom of the
// open list and creates the value in place.
//
// Creation is create-or-return on the backend: a value that already
// exists — by normalized form OR by an explicit alias ("lbs" -> "lb") —
// comes back as the existing row and is simply selected, so a typo can
// never produce a near-duplicate and two admins adding the same brand
// at once converge on one record.
//
// Accessibility: `combobox` + `listbox` roles, arrow/Enter/Escape
// keyboard handling, and 44px-minimum touch targets throughout for the
// tablet and phone layouts.
const KIND_LABELS = {
  brand: "brand",
  unit: "unit",
  family: "product family",
};

export default function AttributeCombobox({
  kind, valueId, valueLabel, onChange, disabled = false,
  label, help, testId, allowClear = true, placeholder,
}) {
  const [options, setOptions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef(null);
  const noun = KIND_LABELS[kind] || kind;

  const load = async () => {
    try {
      const { data } = await api.get("/retail/attribute-values", { params: { kind } });
      setOptions(data?.values || []);
    } catch (_e) {
      setOptions([]);
    } finally {
      setLoaded(true);
    }
  };

  // `load` is re-created on every render; depending on it would refetch
  // the whole picker list on each keystroke. The list only ever needs
  // reloading when the dictionary KIND changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [kind]);

  // Close on an outside click so the panel never sits over the form.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const needle = text.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!needle) return options;
    return options.filter((o) =>
      String(o.value || "").toLowerCase().includes(needle) ||
      (o.aliases || []).some((a) => String(a).includes(needle)),
    );
  }, [options, needle]);

  // Offer creation only for a genuinely new value — never when the
  // typed text already matches an option exactly.
  const exactMatch = options.find(
    (o) => String(o.value || "").toLowerCase() === needle,
  );
  const canCreate = !!needle && !exactMatch && !busy;

  const select = (option) => {
    onChange({ id: option.id, value: option.value });
    setText("");
    setOpen(false);
    setActiveIndex(-1);
  };

  const create = async () => {
    const candidate = text.trim();
    if (!candidate) return;
    setBusy(true);
    try {
      const { data } = await api.post("/retail/attribute-values", { kind, value: candidate });
      const row = data?.value;
      if (!row) return;
      setOptions((prev) => (prev.some((o) => o.id === row.id) ? prev : [...prev, row]));
      if (data.created === false) {
        // Already existed (same normalized value, or a known alias like
        // "lbs" -> "lb"). Select it and say so plainly rather than
        // showing an error the operator cannot act on.
        toast.info(`“${row.value}” is already in your ${noun} list — selected it.`);
      } else {
        toast.success(`Added ${noun} “${row.value}”.`);
      }
      select(row);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.message || `Could not add that ${noun}.`);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault(); setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && filtered[activeIndex]) select(filtered[activeIndex]);
      else if (canCreate) create();
    }
  };

  const displayed = open ? text : (valueLabel || "");

  return (
    <div className="space-y-1" ref={wrapRef}>
      {label && (
        <label className="text-xs font-medium text-slate-700" htmlFor={`${testId}-input`}>
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id={`${testId}-input`}
          data-testid={testId}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-label={label || noun}
          disabled={disabled}
          value={displayed}
          placeholder={placeholder || `Select or add a ${noun}…`}
          onFocus={() => { setOpen(true); setText(""); }}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
          className="w-full h-11 rounded-lg border border-slate-300 pl-3 pr-16 text-sm disabled:bg-slate-100"
        />
        {allowClear && valueId && !open && (
          <button
            type="button"
            onClick={() => onChange({ id: null, value: null })}
            data-testid={`${testId}-clear`}
            aria-label={`Clear ${noun}`}
            className="absolute right-9 top-0 h-11 w-9 grid place-items-center text-slate-400 hover:text-slate-700"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => setOpen((v) => !v)}
          className="absolute right-0 top-0 h-11 w-9 grid place-items-center text-slate-400"
        >
          <ChevronDown className="w-4 h-4" />
        </button>

        {open && (
          <ul
            role="listbox"
            data-testid={`${testId}-options`}
            className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
          >
            {!loaded && (
              <li className="px-3 py-3 text-sm text-slate-500 flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </li>
            )}
            {loaded && filtered.map((o, i) => (
              <li
                key={o.id}
                role="option"
                aria-selected={o.id === valueId}
                data-testid={`${testId}-option-${o.id}`}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(o)}
                className={
                  "min-h-[44px] px-3 py-2 text-sm cursor-pointer flex items-center justify-between gap-2 " +
                  (i === activeIndex ? "bg-slate-100" : "")
                }
              >
                <span className="truncate">{o.value}</span>
                {o.id === valueId && <span className="text-[10px] text-emerald-700">selected</span>}
              </li>
            ))}
            {loaded && filtered.length === 0 && !canCreate && (
              <li className="px-3 py-3 text-sm text-slate-500">No {noun} values yet.</li>
            )}
            {canCreate && (
              <li
                role="option"
                aria-selected={false}
                data-testid={`${testId}-create`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={create}
                className="min-h-[44px] px-3 py-2 text-sm cursor-pointer flex items-center gap-2 border-t border-slate-200 text-emerald-800 font-medium"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Add “{text.trim()}” as a new {noun}
              </li>
            )}
          </ul>
        )}
      </div>
      {help && <p className="text-[10px] text-slate-500">{help}</p>}
    </div>
  );
}
